import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import type { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { UserAccountEntryEntry } from '@sorare/core/src/__generated__/globalTypes';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import {
  AccountEntry,
  messages,
} from '@sorare/core/src/components/mySorare/TransactionsHistory/UserAccountEntries/AccountEntry';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { FeesDetailsTooltip } from 'components/offer/FeesDetailsTooltip';

import type { Offer_userAccountEntry } from './__generated__/index.graphql';
import { TransactionsOffer } from '../../common/TransactionsOffer';

type Props = {
  userAccountEntry: Offer_userAccountEntry;
  userAccountEntryFees?: Offer_userAccountEntry;
  primaryCurrency: Currency;
};

export const Offer = ({
  userAccountEntry,
  userAccountEntryFees,
  primaryCurrency,
}: Props) => {
  const { tokenOperation } = userAccountEntry;

  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const { toMonetaryAmount } = useMonetaryAmount();

  if (
    !currentUser ||
    !tokenOperation ||
    userAccountEntry.entryType === UserAccountEntryEntry.PAYMENT_FEE
  ) {
    return null;
  }

  if (!isType(tokenOperation, 'TokenOffer') || !tokenOperation.sender) {
    return null;
  }

  const receiver = tokenOperation.receiver || tokenOperation.actualReceiver;

  if (
    (receiver && !isType(receiver, 'User')) ||
    !isType(tokenOperation.sender, 'User')
  ) {
    return null;
  }

  const amounts = toMonetaryAmount(userAccountEntry.amounts);

  const tokenOperationFeeMonetary =
    tokenOperation?.marketFeeAmounts &&
    toMonetaryAmount(tokenOperation.marketFeeAmounts);

  const marketFeeAmounts =
    userAccountEntryFees?.amounts &&
    toMonetaryAmount(userAccountEntryFees.amounts);

  const receiverSideAmounts = toMonetaryAmount(
    tokenOperation.receiverSide.amounts
  );

  const senderSideAmounts = toMonetaryAmount(tokenOperation.senderSide.amounts);

  const data =
    tokenOperation.sender.slug === currentUser.slug
      ? {
          title: messages.offerTitleAsSender,
          otherParty: receiver,
          receiveAmounts: receiverSideAmounts,
          sendAmounts: senderSideAmounts,
        }
      : {
          title: messages.offerTitleAsReceiver,
          otherParty: tokenOperation.sender,
          receiveAmounts: senderSideAmounts,
          sendAmounts: receiverSideAmounts,
        };

  const totalAmounts = Sum([amounts, marketFeeAmounts || MonetaryAmount.zero]);

  const userAccountEntryWithFees = {
    ...userAccountEntry,
    amounts: {
      __typename: 'MonetaryAmount' as const,
      ...totalAmounts,
      referenceCurrency: userAccountEntry.amounts.referenceCurrency,
    },
  };

  return (
    <AccountEntry
      userAccountEntry={userAccountEntryWithFees}
      primaryCurrency={primaryCurrency}
      tooltip={
        !!data.receiveAmounts.isPositive &&
        !!tokenOperationFeeMonetary?.isPositive && (
          <LinkOther>
            <FeesDetailsTooltip
              completed
              monetaryAmount={data.receiveAmounts}
              marketFeeMonetaryAmount={
                (tokenOperation?.marketFeeAmounts &&
                  toMonetaryAmount(tokenOperation.marketFeeAmounts)) ||
                MonetaryAmount.zero
              }
              referenceCurrency={userAccountEntry.amounts.referenceCurrency}
            />
          </LinkOther>
        )
      }
      title={formatMessage(data.title, {
        otherParty: data.otherParty && <UserName user={data.otherParty} />,
      })}
    >
      <TransactionsOffer tokenOffer={tokenOperation} />
    </AccountEntry>
  );
};

Offer.fragments = {
  userAccountEntry: gql`
    fragment Offer_userAccountEntry on UserAccountEntry {
      id
      aasmState
      entryType
      amounts {
        ...useMonetaryAmount_monetaryAmount
      }
      tokenOperation {
        ... on TokenOffer {
          id
          marketFeeAmounts {
            ...useMonetaryAmount_monetaryAmount
          }
          receiverSide {
            id
            amounts {
              ...useMonetaryAmount_monetaryAmount
            }
          }
          actualReceiver {
            ... on User {
              slug
              ...UserName_publicUserInfoInterface
            }
            ... on AnonymousUser {
              id
            }
          }
          receiver {
            ... on User {
              slug
              ...UserName_publicUserInfoInterface
            }
            ... on AnonymousUser {
              id
            }
          }
          sender {
            ... on User {
              slug
              ...UserName_publicUserInfoInterface
            }
            ... on AnonymousUser {
              id
            }
          }
          senderSide {
            id
            amounts {
              ...useMonetaryAmount_monetaryAmount
            }
          }
          ...TransactionsOffer_tokenOffer
        }
      }
      ...AccountEntry_userAccountEntry
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${AccountEntry.fragments.userAccountEntry}
    ${TransactionsOffer.fragments.tokenOffer}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<Offer_userAccountEntry>,
};
